import { useEffect, useRef } from "react";
import { KarlAskCard } from "../KarlAskCard";
import type { KarlAnswer, KarlAsk, KarlAskTool } from "../karlAsks";
import type { KarlAskQueueView } from "../karlAskQueue";
import type { ActiveCableTool } from "../edits/useCableDirectEdit";
import type {
  KarlBoardControls,
  KarlDiagramControl,
  KarlPlanControl,
  KarlShapeControls,
} from "./types";

interface KarlAskQueueProps {
  view: KarlAskQueueView;
  isFinished: boolean;
  onAnswer: (ask: KarlAsk, answer: KarlAnswer) => void;
  /** Runs a tool on the ask's subject without answering the question. */
  onRunTool: (ask: KarlAsk, tool: KarlAskTool) => void;
  onBack: () => void;
  onForward: () => void;
  /** Reopens a finished group at its first ask, keeping every answer. */
  onWalkAgain?: () => void;
  isPicking?: boolean;
  pickedCount?: number;
  onConfirmPick?: () => void;
  /** The run whose shape is open for editing, so Reshape can light up. */
  /** The reshape mode's own controls, while a run's graph is open. */
  shapeControls?: KarlShapeControls;
  /** The one tool that is on, across the mode and the arms alike. */
  activeTool?: ActiveCableTool | null;
  onCancelArm: () => void;
  plan?: KarlPlanControl;
  /** Lets a route ask offer the schematic page its way is drawn on. */
  diagram?: KarlDiagramControl;
  isActive?: boolean;
  boardControls?: KarlBoardControls;
  /** Called once this group has nothing left to ask and nothing armed. */
  onFinish?: () => void;
}

/**
 * The card, and nothing under it. The plan-wide caveats that used to sit here
 * were the same four lines beside all 83 questions — "this is redundant for
 * now". What belongs under a route is that route's own facts, and they are on
 * the card itself.
 */
export const KarlAskQueue = ({
  view,
  isFinished,
  onAnswer,
  onRunTool,
  onBack,
  onForward,
  onWalkAgain,
  onCancelArm,
  isPicking,
  pickedCount,
  onConfirmPick,
  shapeControls,
  activeTool,
  plan,
  diagram,
  isActive = true,
  boardControls,
  onFinish,
}: KarlAskQueueProps) => {
  // Held in a ref, not a dependency: the panel passes a fresh arrow every
  // render, and depending on it re-fired the effect on each one — which set
  // state in the panel, which re-rendered, forever, at the exact moment the
  // walkthrough finished.
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  // Only when the group is genuinely done. Firing on an empty list would also
  // fire mid-arm, while the estimator is still on the drawing.
  useEffect(() => {
    if (isFinished) onFinishRef.current?.();
  }, [isFinished]);

  return (
    <KarlAskCard
      view={view}
      onAnswer={onAnswer}
      onRunTool={onRunTool}
      onBack={onBack}
      onForward={onForward}
      onWalkAgain={onWalkAgain}
      isPicking={isPicking}
      pickedCount={pickedCount}
      onConfirmPick={onConfirmPick}
      shapeControls={shapeControls}
      activeTool={activeTool}
      onCancelArm={onCancelArm}
      plan={plan}
      diagram={diagram}
      isActive={isActive}
      boardControls={boardControls}
    />
  );
};
